/**
 * =============================================================================
 * CHAT LOG ENTRY COMPONENT
 * =============================================================================
 * 
 * Renders a single log entry in the chat window.
 * Shows: Timestamp, User, Action taken
 * 
 * LOG TYPES (determines styling):
 * - action_selected: User clicked an action button
 * - action_confirmed: User confirmed an action
 * - action_rejected: User rejected an action
 * - alert_snoozed: Alert was snoozed
 * - alert_deleted: Alert was deleted
 * - edit_started: User started editing
 * - edit_completed: User completed editing
 * - edit_cancelled: User cancelled editing
 * - system: System message
 * 
 * DATA MAPPING:
 * - Line 60: logType → Icon and color
 * - Line 85: message → Log text
 * - Line 90: user → Username display
 * - Line 95: timestamp → Formatted time
 */

import { Box, Text, Group } from '@mantine/core';
import {
    Lightning,
    CheckCircle,
    XCircle,
    ClockCounterClockwise,
    Trash,
    Pencil,
    Info,
} from '@phosphor-icons/react';
import { memo, useMemo } from 'react';
import { motion } from 'framer-motion';
import type { ChatLogType } from './types';

interface ChatLogEntryProps {
    /** Type of log (determines styling) */
    logType: ChatLogType;
    /** Log message text */
    message: string;
    /** User who performed the action */
    user?: string;
    /** ISO timestamp */
    timestamp: string;
}

// =============================================================================
// CONFIGURATION - Line 60
// Maps logType to icon and color
// =============================================================================

const logConfig: Record<ChatLogType, { 
    icon: React.ReactNode; 
    color: string;
    bgColor: string;
    borderColor: string;
}> = {
    action_selected: {
        icon: <Lightning size={12} weight="fill" />,
        color: 'text-blu-800',
        bgColor: 'bg-blu-50',
        borderColor: 'border-blu-200',
    },
    action_confirmed: {
        icon: <CheckCircle size={12} weight="fill" />,
        color: 'text-green',
        bgColor: 'bg-green/10',
        borderColor: 'border-green/20',
    },
    action_rejected: {
        icon: <XCircle size={12} weight="fill" />,
        color: 'text-halt-800',
        bgColor: 'bg-halt-400/20',
        borderColor: 'border-halt-800/20',
    },
    alert_snoozed: {
        icon: <ClockCounterClockwise size={12} weight="fill" />,
        color: 'text-purple',
        bgColor: 'bg-purple/10',
        borderColor: 'border-purple/20',
    },
    alert_deleted: {
        icon: <Trash size={12} weight="fill" />,
        color: 'text-halt-800',
        bgColor: 'bg-halt-400/20',
        borderColor: 'border-halt-800/20',
    },
    edit_started: {
        icon: <Pencil size={12} weight="fill" />,
        color: 'text-mustard-900',
        bgColor: 'bg-mustard-800/10',
        borderColor: 'border-mustard-800/20',
    },
    edit_completed: {
        icon: <CheckCircle size={12} weight="fill" />,
        color: 'text-green',
        bgColor: 'bg-green/10',
        borderColor: 'border-green/20',
    },
    edit_cancelled: {
        icon: <XCircle size={12} weight="fill" />,
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
    },
    system: {
        icon: <Info size={12} weight="fill" />,
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
    },
};

// =============================================================================
// COMPONENT
// =============================================================================

const ChatLogEntry: React.FC<ChatLogEntryProps> = memo(({
    logType,
    message,
    user,
    timestamp,
}) => {
    const config = logConfig[logType] || logConfig.system;

    // Format timestamp
    const formattedTime = useMemo(() => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
        });
    }, [timestamp]);

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
        >
            <Box
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${config.bgColor} ${config.borderColor}`}
            >
                {/* Icon */}
                <Box className={config.color}>
                    {config.icon}
                </Box>

                {/* Content */}
                <Group className="flex-1 justify-between items-center gap-3">
                    {/* ========================================================
                        MESSAGE - Line 85
                        The log message text
                        ======================================================== */}
                    <Text className="text-xs text-gray-700 flex-1">
                        {/* ====================================================
                            USER - Line 90
                            Username who performed action
                            ==================================================== */}
                        {user && (
                            <Text component="span" className="font-semibold text-gray-900">
                                {user}:{' '}
                            </Text>
                        )}
                        {message}
                    </Text>

                    {/* ========================================================
                        TIMESTAMP - Line 95
                        Formatted time (HH:MM)
                        ======================================================== */}
                    <Text className="text-xs text-gray-400 flex-shrink-0">
                        {formattedTime}
                    </Text>
                </Group>
            </Box>
        </motion.div>
    );
});

ChatLogEntry.displayName = 'ChatLogEntry';

export default ChatLogEntry;
